import axios from 'axios';
import http from '../http';
import { validateImageFile } from '../imageUploadPolicy';

const PROCESSING_VERSION = 2;
const COMPLETE_RETRY_DELAY_MS = 300;

function wait(milliseconds) {
  return new Promise(resolve => setTimeout(resolve, milliseconds));
}

function createUploadRequest(file, originalFile, albumId, dimensions, sourceFolderName = null) {
  return {
    processingVersion: PROCESSING_VERSION,
    albumId,
    fileName: file.name,
    originalFileName: originalFile.name,
    originalFileSize: originalFile.size,
    fileSize: file.size,
    width: dimensions.width,
    height: dimensions.height,
    sourceFolderName: typeof sourceFolderName === 'string' && sourceFolderName.trim()
      ? sourceFolderName.trim()
      : null,
  };
}

function createOssFormData(upload, file) {
  const formData = new FormData();
  Object.entries(upload.fields || {}).forEach(([name, value]) => {
    formData.append(name, value);
  });
  // OSS PostPolicy requires the file to be the final multipart field.
  formData.append('file', file);
  return formData;
}

async function postOriginalToOss(upload, file, onUploadProgress) {
  if (!upload?.url || !upload?.fields) {
    const error = new Error('服务端未返回有效的上传表单。');
    error.code = 'INVALID_UPLOAD_FORM';
    throw error;
  }
  return axios.post(upload.url, createOssFormData(upload, file), { onUploadProgress });
}

// Endpoints used by the photographer's own upload. Sign-in-free agent upload
// pages pass their own set (same request and response shapes).
const PHOTOGRAPHER_UPLOAD_API = {
  client: http,
  addPath: '/user/picture/addToAlbum',
  completePath: pictureId => `/user/picture/${pictureId}/complete`,
  removePicture: (pictureId, orderId) => http.post('/user/picture/delete', {
    orderId,
    pictureIds: [pictureId],
  }),
};

function getErrorCode(error) {
  return error?.response?.data?.code || error?.code;
}

async function completePictureUpload(pictureId, api = PHOTOGRAPHER_UPLOAD_API) {
  try {
    return await api.client.post(api.completePath(pictureId));
  } catch (error) {
    if (error?.response?.status !== 409 || getErrorCode(error) !== 'UPLOAD_NOT_FOUND') throw error;
    await wait(COMPLETE_RETRY_DELAY_MS);
    return api.client.post(api.completePath(pictureId));
  }
}

async function uploadPictureFile({ uuid, albumId, file, originalFile = file, dimensions, sourceFolderName = null, orderId, onProgress, api = PHOTOGRAPHER_UPLOAD_API }) {
  onProgress(uuid, 5, '检查图片');
  const resolvedDimensions = dimensions || await validateImageFile(file);
  const request = createUploadRequest(file, originalFile, albumId, resolvedDimensions, sourceFolderName);

  onProgress(uuid, 15, '创建上传任务');
  let creation = await api.client.post(api.addPath, request);
  if (creation.data.ifExist) return { status: 'exist', picture: creation.data.picture };

  let { picture, upload } = creation.data;
  const progressHandler = (progressEvent) => {
    const total = progressEvent.total || file.size;
    const percent = total > 0
      ? Math.min(90, Math.max(20, Math.round(20 + progressEvent.loaded * 70 / total)))
      : 20;
    onProgress(uuid, percent, '上传图片');
  };

  try {
    onProgress(uuid, 20, '上传图片');
    try {
      await postOriginalToOss(upload, file, progressHandler);
    } catch (firstError) {
      onProgress(uuid, undefined, '上传失败，正在重试 1/1');
      creation = await api.client.post(api.addPath, request);
      if (creation.data.ifExist) return { status: 'exist', picture: creation.data.picture };
      picture = creation.data.picture;
      upload = creation.data.upload;
      await postOriginalToOss(upload, file, progressHandler);
    }
  } catch (error) {
    if (picture?.id && orderId && api.removePicture) {
      await api.removePicture(picture.id, orderId).catch(() => {});
    }
    throw error;
  }

  onProgress(uuid, 94, '校验图片');
  const completed = await completePictureUpload(picture.id, api);
  return {
    status: 'success',
    picture: completed.data.picture,
    alreadyCompleted: Boolean(completed.data.alreadyCompleted),
  };
}

export {
  COMPLETE_RETRY_DELAY_MS,
  PHOTOGRAPHER_UPLOAD_API,
  PROCESSING_VERSION,
  completePictureUpload,
  createOssFormData,
  createUploadRequest,
  postOriginalToOss,
  uploadPictureFile,
};
